import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileCheck, Plus, Search, ArrowLeft, ArrowRight, Calendar, Users, CheckCircle2,
  ClipboardList, Clock, Award, ShieldCheck, TrendingUp,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// =========================================================================
// EXAM LIST (Super Admin)
// =========================================================================
export function AdminExamListPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", exam_type: "all", search: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 100 };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/admin/exams", { params });
      setRows(data.data.exams);
      setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="Exams" breadcrumbs={["Super Admin", "Exams"]}>
      <div className="space-y-4" data-testid="admin-exams-list">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/app/super-admin/exams/new" className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="btn-new-exam">
            <Plus className="h-3.5 w-3.5" /> New exam
          </Link>
          <div className="ml-auto text-xs text-slate-500">Total: <span className="font-bold">{total}</span></div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-4">
          <div className="relative sm:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input placeholder="Search exam title or code…" value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm" data-testid="exam-search" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.exam_type} onValueChange={(v) => setFilters((p) => ({ ...p, exam_type: v }))}>
            <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="practice">Practice</SelectItem>
              <SelectItem value="final_assessment">Final assessment</SelectItem>
              <SelectItem value="certification">Certification</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
              <SelectItem value="mock">Mock</SelectItem>
              <SelectItem value="pre_test">Pre-Test</SelectItem>
              <SelectItem value="post_test">Post-Test</SelectItem>
              <SelectItem value="live_test">Live Test</SelectItem>
              <SelectItem value="assignment_based">Assignment</SelectItem>
              <SelectItem value="competency">Competency</SelectItem>
              <SelectItem value="practice_quiz">Practice Quiz</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading ? <LoadingBlock label="Loading exams…" /> : rows.length === 0 ? (
          <EmptyState title="No exams yet" description="Create your first exam to link with a course." icon={FileCheck} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((e) => (
              <div key={e.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition" data-testid={`exam-card-${e.id}`}>
                <div className="h-24 bg-gradient-to-br from-[#101876] to-[#329112] p-4 text-white">
                  <div className="text-xs font-bold uppercase tracking-widest opacity-80">{e.exam_type?.replace(/_/g, " ")}</div>
                  <div className="mt-1 text-sm font-bold line-clamp-2">{e.exam_title}</div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-mono">{e.exam_code}</span>
                    <StatusBadge status={e.status} />
                  </div>
                  <div className="text-xs text-slate-500 line-clamp-1">{e.course_title}</div>
                  <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
                    <div><div className="font-bold text-slate-800">{e.total_questions_to_display}</div><div className="text-slate-500">Questions</div></div>
                    <div><div className="font-bold text-slate-800">{e.duration_minutes}m</div><div className="text-slate-500">Duration</div></div>
                    <div><div className="font-bold text-slate-800">{e.passing_percentage}%</div><div className="text-slate-500">Pass</div></div>
                  </div>
                  <Link to={`/app/super-admin/exams/${e.id}`} className="block pt-2 text-xs font-bold text-[#101876] hover:underline" data-testid={`exam-open-${e.id}`}>
                    Open exam →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// EXAM FORM (create/edit)
// =========================================================================
export function AdminExamFormPage({ mode = "create" }) {
  const { examId } = useParams();
  const nav = useNavigate();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    course_id: "", exam_title: "", exam_code: "", exam_description: "",
    exam_type: "final_assessment", exam_mode: "self_paced",
    total_questions_to_display: 20, total_marks: 20, passing_percentage: 60,
    duration_minutes: 30, maximum_attempts: 3, cooling_period_minutes: 0,
    mandatory_exam: false, result_display_mode: "instant",
    answer_review_allowed: true, show_correct_answers: true, show_explanation: true,
    randomize_questions: true, randomize_options: true,
    allow_back_navigation: true, one_question_per_screen: true,
    auto_submit_on_time_expiry: true, negative_marking_enabled: false,
    tab_switch_allowed_count: 3, fullscreen_required: false,
    copy_paste_disabled: true, auto_submit_on_violation: false,
    // Phase E1 — Graphy-style flags
    allow_post_test_pdf_download: true,
    include_answer_key_in_learner_pdf: false,
    include_answer_key_in_admin_pdf: true,
    certificate_eligibility_exam: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/courses", { params: { page_size: 500, status: "published" } });
        setCourses(data.data.courses);
      } catch { /* */ }
    })();
    if (mode === "edit" && examId) {
      (async () => {
        try {
          const { data } = await api.get(`/admin/exams/${examId}`);
          const e = data.data.exam;
          setForm((p) => ({ ...p, ...e }));
        } catch (err) { toast.error(formatApiError(err)); }
      })();
    }
  }, [mode, examId]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["total_questions_to_display", "total_marks", "passing_percentage",
       "duration_minutes", "maximum_attempts", "cooling_period_minutes",
       "tab_switch_allowed_count"].forEach((k) => {
        payload[k] = Number(payload[k]) || 0;
      });
      if (mode === "edit") {
        await api.put(`/admin/exams/${examId}`, payload);
      } else {
        const { data } = await api.post("/admin/exams", payload);
        toast.success("Exam created.");
        nav(`/app/super-admin/exams/${data.data.exam.id}`);
        return;
      }
      toast.success("Saved.");
      nav(`/app/super-admin/exams/${examId}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const field = (k, label, extra = {}) => (
    <label className="block text-sm">
      <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <input value={form[k] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`ef-${k}`} {...extra} />
    </label>
  );

  const switchRow = (k, label, hint) => (
    <label key={k} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm cursor-pointer" data-testid={`ef-${k}`}>
      <div>
        <div className="font-medium">{label}</div>
        {hint && <div className="text-xs text-slate-500">{hint}</div>}
      </div>
      <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.checked }))} />
    </label>
  );

  return (
    <DashboardLayout title={mode === "edit" ? "Edit exam" : "New exam"} breadcrumbs={["Super Admin", "Exams", mode]}>
      <div className="max-w-5xl space-y-4" data-testid="exam-form">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Basic</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Course *</div>
              <select value={form.course_id} disabled={mode === "edit"}
                onChange={(e) => setForm((p) => ({ ...p, course_id: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="ef-course-id">
                <option value="">Select course</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.course_code} — {c.course_title}</option>)}
              </select>
            </label>
            {field("exam_title", "Exam title *")}
            {field("exam_code", "Exam code *", { disabled: mode === "edit" })}
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Exam type</div>
              <select value={form.exam_type} onChange={(e) => setForm((p) => ({ ...p, exam_type: e.target.value }))}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="ef-exam_type">
                <option value="practice">Practice</option>
                <option value="final_assessment">Final Assessment</option>
                <option value="certification">Certification</option>
                <option value="compliance">Compliance</option>
                <option value="department_assessment">Department Assessment</option>
                <option value="mock">Mock Test</option>
                <option value="pre_test">Pre-Test (Graphy)</option>
                <option value="post_test">Post-Test (Graphy)</option>
                <option value="live_test">Live Test (Graphy)</option>
                <option value="assignment_based">Assignment-based (Graphy)</option>
                <option value="competency">Competency (Graphy)</option>
                <option value="practice_quiz">Practice Quiz (Graphy)</option>
              </select>
            </label>
          </div>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Description</div>
            <textarea rows={2} value={form.exam_description || ""} onChange={(e) => setForm((p) => ({ ...p, exam_description: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Question paper</div>
          <div className="grid gap-3 sm:grid-cols-3">
            {field("total_questions_to_display", "Questions to display", { type: "number" })}
            {field("total_marks", "Total marks", { type: "number" })}
            {field("passing_percentage", "Passing %", { type: "number" })}
            {field("duration_minutes", "Duration (min)", { type: "number" })}
            {field("maximum_attempts", "Max attempts", { type: "number" })}
            {field("cooling_period_minutes", "Cooling period (min)", { type: "number" })}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {switchRow("randomize_questions", "Randomize questions")}
            {switchRow("randomize_options", "Randomize option order")}
            {switchRow("allow_back_navigation", "Allow back navigation")}
            {switchRow("one_question_per_screen", "One question per screen")}
            {switchRow("mandatory_exam", "Mandatory exam")}
            {switchRow("negative_marking_enabled", "Negative marking")}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Result & Review</div>
          <label className="block text-sm max-w-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Result display mode</div>
            <select value={form.result_display_mode} onChange={(e) => setForm((p) => ({ ...p, result_display_mode: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option value="instant">Instant</option>
              <option value="delayed">Delayed</option>
              <option value="admin_review">Admin review required</option>
            </select>
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {switchRow("answer_review_allowed", "Answer review allowed")}
            {switchRow("show_correct_answers", "Show correct answers in review")}
            {switchRow("show_explanation", "Show explanation in review")}
            {switchRow("auto_submit_on_time_expiry", "Auto-submit on time expiry")}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Post-Test PDF & Certification</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {switchRow("allow_post_test_pdf_download", "Allow post-test PDF download", "Learners get a downloadable PDF report after each attempt.")}
            {switchRow("include_answer_key_in_learner_pdf", "Include answer key in learner PDF", "When enabled, learner PDF shows correct answers & explanations.")}
            {switchRow("include_answer_key_in_admin_pdf", "Include answer key in admin PDF", "Admin/Hospital Admin PDF export always includes answer key by default.")}
            {switchRow("certificate_eligibility_exam", "Counts toward course certificate", "Marking this ensures passing this exam is required to earn the course certificate.")}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Security</div>
          <div className="grid gap-3 sm:grid-cols-3">
            {field("tab_switch_allowed_count", "Tab switch allowed", { type: "number" })}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {switchRow("fullscreen_required", "Fullscreen required")}
            {switchRow("copy_paste_disabled", "Disable copy / paste")}
            {switchRow("auto_submit_on_violation", "Auto-submit on violation")}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
          <button onClick={() => nav(-1)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Cancel
          </button>
          <button disabled={saving} onClick={submit} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="ef-save">
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create as draft"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// EXAM DETAIL (Admin)
// =========================================================================
export function AdminExamDetailPage() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [attempts, setAttempts] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get(`/admin/exams/${examId}`);
      setData(r.data.exam);
    } catch (err) { toast.error(formatApiError(err)); }
    try {
      const { data: a } = await api.get(`/admin/exam-assignments`, { params: { exam_id: examId } });
      setAssignments(a.data.assignments);
    } catch { /* */ }
    try {
      const { data: r } = await api.get(`/admin/exam-results`, { params: { exam_id: examId, page_size: 20 } });
      setAttempts(r.data.attempts);
    } catch { /* */ }
  }, [examId]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    try {
      await api.patch(`/admin/exams/${examId}/status`, { status: "published" });
      toast.success("Exam published.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const suspend = async () => {
    const reason = prompt("Suspend reason?");
    if (!reason) return;
    try {
      await api.patch(`/admin/exams/${examId}/status`, { status: "suspended", reason });
      toast.success("Suspended."); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <DashboardLayout title="Exam"><LoadingBlock /></DashboardLayout>;

  return (
    <DashboardLayout title={data.exam_title} breadcrumbs={["Super Admin", "Exams", data.exam_code]}>
      <div className="space-y-6" data-testid="exam-detail">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
          <StatusBadge status={data.status} />
          <span className="font-mono text-xs text-slate-500">{data.exam_code}</span>
          <div className="ml-auto flex gap-2">
            <Link to={`/app/super-admin/exams/${examId}/edit`} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">Edit</Link>
            {data.status === "draft" && (
              <button onClick={publish} disabled={!data.is_ready} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-40" data-testid="btn-publish-exam">
                Publish
              </button>
            )}
            {data.status === "published" && (
              <>
                <button onClick={() => setAssignDialog(true)} className="rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="btn-assign-exam">Assign</button>
                <button onClick={suspend} className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">Suspend</button>
              </>
            )}
          </div>
        </div>
        {!data.is_ready && data.status === "draft" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            Only <b>{data.available_questions}</b> approved questions available in the course. This exam needs <b>{data.total_questions_to_display}</b> to publish.
          </div>
        )}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Questions to display" value={data.total_questions_to_display} icon="HelpCircle" tone="navy" testId="ed-kpi-q" index={0} />
          <StatCard label="Duration" value={`${data.duration_minutes} min`} icon="Clock" tone="green" testId="ed-kpi-d" index={1} />
          <StatCard label="Passing %" value={`${data.passing_percentage}%`} icon="ShieldCheck" tone="navy" testId="ed-kpi-p" index={2} />
          <StatCard label="Max attempts" value={data.maximum_attempts} icon="Layers" tone="slate" testId="ed-kpi-m" index={3} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-2">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Details</div>
          <div className="text-sm text-slate-700">{data.exam_description}</div>
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-3 text-xs text-slate-500">
            <div>Course: <b className="text-slate-800">{data.course?.course_title}</b></div>
            <div>Type: <b className="text-slate-800 capitalize">{data.exam_type?.replace(/_/g, " ")}</b></div>
            <div>Mode: <b className="text-slate-800 capitalize">{data.exam_mode?.replace(/_/g, " ")}</b></div>
            <div>Randomize Qs: <b>{data.randomize_questions ? "Yes" : "No"}</b></div>
            <div>Randomize options: <b>{data.randomize_options ? "Yes" : "No"}</b></div>
            <div>Result display: <b>{data.result_display_mode}</b></div>
            <div>Answer review: <b>{data.answer_review_allowed ? "Yes" : "No"}</b></div>
            <div>Neg marking: <b>{data.negative_marking_enabled ? "Yes" : "No"}</b></div>
            <div>Available approved questions: <b>{data.available_questions}</b></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-bold">Assignments ({assignments.length})</div>
          </div>
          {assignments.length === 0 ? (
            <div className="p-6 text-xs text-slate-500 text-center">No assignments yet. Click Assign to add.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Assigned</th><th className="px-4 py-3 text-left">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assignments.map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-2 font-medium">{a.user?.full_name || "—"}</td>
                    <td className="px-4 py-2 text-xs">{a.user?.email}</td>
                    <td className="px-4 py-2 text-xs">{a.assigned_at?.slice(0, 10)}</td>
                    <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-bold">Recent attempts ({attempts.length})</div>
          </div>
          {attempts.length === 0 ? (
            <div className="p-6 text-xs text-slate-500 text-center">No attempts yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Attempt</th><th className="px-4 py-3 text-left">Score</th><th className="px-4 py-3 text-left">Result</th><th className="px-4 py-3 text-right">Report</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attempts.map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-2 font-medium">{a.user?.full_name}</td>
                    <td className="px-4 py-2 text-xs">#{a.attempt_number}</td>
                    <td className="px-4 py-2 text-xs">{a.marks_obtained}/{a.total_marks} · {a.percentage}%</td>
                    <td className="px-4 py-2"><StatusBadge status={a.pass_fail_status || a.status} /></td>
                    <td className="px-4 py-2 text-xs text-right">
                      <button onClick={async () => {
                        try {
                          const { data: blob } = await api.get(`/admin/exam-attempts/${a.id}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = `attempt_${a.attempt_number}_${data.exam_code}_admin.pdf`;
                          link.click();
                          URL.revokeObjectURL(url);
                        } catch (err) { toast.error(formatApiError(err)); }
                      }} className="text-[#101876] font-bold hover:underline" data-testid={`btn-admin-pdf-${a.id}`}>PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AssignDialog open={assignDialog} onOpenChange={setAssignDialog} examId={examId} onDone={load} />
    </DashboardLayout>
  );
}

function AssignDialog({ open, onOpenChange, examId, onDone }) {
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/organizations", { params: { page_size: 100 } });
        setOrgs(data.data.organizations || data.data.rows || []);
      } catch { /* */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!org) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/staff", { params: { organization_id: org, page_size: 300 } });
        setUsers(data.data.users || data.data.staff || data.data.rows || []);
      } catch { /* */ }
    })();
  }, [org]);

  const toggle = (uid) => setSelected((p) => p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]);

  const submit = async () => {
    if (!selected.length) return toast.error("Select at least one user.");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/exams/${examId}/assign/individual`, {
        user_ids: selected, due_date: dueDate || null,
      });
      toast.success(`Assigned to ${data.data.created} users (skipped ${data.data.skipped}).`);
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Assign exam to users</DialogTitle></DialogHeader>
        <div className="space-y-3" data-testid="assign-dialog">
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Organization</div>
            <select value={org} onChange={(e) => setOrg(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option value="">Select organization</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.organization_name}</option>)}
            </select>
          </label>
          {users.length > 0 && (
            <div className="rounded-md border border-slate-200">
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="assign-select-all"
                    checked={selected.length === users.length && users.length > 0}
                    ref={(el) => { if (el) el.indeterminate = selected.length > 0 && selected.length < users.length; }}
                    onChange={(e) => setSelected(e.target.checked ? users.map((u) => u.id) : [])}
                  />
                  {selected.length === users.length ? "Deselect all" : "Select all"}
                </label>
                <span className="text-[11px] font-semibold text-slate-500" data-testid="assign-selected-count">
                  {selected.length} / {users.length} selected
                </span>
              </div>
              <div className="max-h-60 overflow-auto">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} />
                    <span>{u.full_name}</span>
                    <span className="text-xs text-slate-500">{u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Due date (optional)</div>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button disabled={submitting} onClick={submit} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="assign-submit">
            {submitting ? "Assigning…" : `Assign to ${selected.length} users`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// EXAM RESULTS (Admin cross-tenant)
// =========================================================================
export function AdminExamResultsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ result: "all" });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { page_size: 100 };
        if (filter.result !== "all") params.result = filter.result;
        const { data } = await api.get("/admin/exam-results", { params });
        setRows(data.data.attempts);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [filter]);

  return (
    <DashboardLayout title="Exam Results" breadcrumbs={["Super Admin", "Exam Results"]}>
      <div className="space-y-4" data-testid="admin-results">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <Select value={filter.result} onValueChange={(v) => setFilter((p) => ({ ...p, result: v }))}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All results</SelectItem>
              <SelectItem value="passed">Passed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={async () => {
            try {
              const params = { format: "xlsx" };
              if (filter.result !== "all") params.result = filter.result;
              const { data } = await api.get("/admin/exam-results/export", { params, responseType: "blob" });
              const url = URL.createObjectURL(new Blob([data]));
              const link = document.createElement("a");
              link.href = url;
              link.download = `exam_results.xlsx`;
              link.click();
              URL.revokeObjectURL(url);
            } catch (err) { toast.error(formatApiError(err)); }
          }} className="ml-auto rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid="btn-export-results">
            <ArrowRight className="h-3.5 w-3.5" /> Export XLSX
          </button>
        </div>
        {loading ? <LoadingBlock label="Loading…" /> : rows.length === 0 ? (
          <EmptyState title="No attempts yet" description="Attempts will appear here after learners begin exams." icon={ClipboardList} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Learner</th><th className="px-4 py-3 text-left">Exam</th><th className="px-4 py-3 text-left">Attempt</th><th className="px-4 py-3 text-left">Score</th><th className="px-4 py-3 text-left">%</th><th className="px-4 py-3 text-left">Result</th><th className="px-4 py-3 text-left">Submitted</th><th className="px-4 py-3 text-right">PDF</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`result-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.user?.full_name}<div className="text-xs text-slate-500">{r.user?.email}</div></td>
                    <td className="px-4 py-2 text-xs">{r.exam?.exam_title}<div className="font-mono text-[10px] text-slate-500">{r.exam?.exam_code}</div></td>
                    <td className="px-4 py-2 text-xs">#{r.attempt_number}</td>
                    <td className="px-4 py-2 text-xs">{r.marks_obtained}/{r.total_marks}</td>
                    <td className="px-4 py-2 text-xs font-bold">{r.percentage}%</td>
                    <td className="px-4 py-2"><StatusBadge status={r.pass_fail_status || r.status} /></td>
                    <td className="px-4 py-2 text-xs">{r.submitted_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2 text-xs text-right">
                      <button onClick={async () => {
                        try {
                          const { data } = await api.get(`/admin/exam-attempts/${r.id}/pdf`, { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = `attempt_${r.attempt_number}_${r.exam?.exam_code}.pdf`;
                          link.click();
                          URL.revokeObjectURL(url);
                        } catch (err) { toast.error(formatApiError(err)); }
                      }} className="text-[#101876] font-bold hover:underline" data-testid={`res-pdf-${r.id}`}>PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
